import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, of, tap } from 'rxjs';
import { FieldExpression, NodeExpression, phrase, toString as luceneToString } from 'lucene';

import { PaginatedResult } from '../../shared/models/paginated-result';
import { ORDER_SERVER_URL } from '../../config';
import { ClaimBatch, FieldOfClaimBatch } from '../models/claim-batch';
import { OrderBy, OrderByDirection } from '../../shared/utils/constant';
import { batchesFilter } from '../utils/batches.utils';
import { QualityBatch } from '../models/quality-batch';
import { unpadSoldto } from '../../shared/utils/padding';

import { AllQualityIssueService } from './all-quality-issue.service';

const GET_BATCHES_URL = `${ ORDER_SERVER_URL }/v4/claim-batches`;

@Injectable({ providedIn: 'root' })
export class ClaimBatchesService {
	constructor(
		private readonly http: HttpClient,
		private readonly claimService: AllQualityIssueService
	) {}

	getBatches(
		fields: readonly FieldOfClaimBatch[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof ClaimBatch>[] | OrderBy<keyof ClaimBatch>,
		onlyDistinct: boolean
	) {
		const url = new URL(GET_BATCHES_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<ClaimBatch>>(url.toString());
	}

	getBatchById(
		batchId: string,
		fields: readonly FieldOfClaimBatch[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof ClaimBatch>[] | OrderBy<keyof ClaimBatch>,
		onlyDistinct: boolean
	) {
		const url = new URL(`${ GET_BATCHES_URL }/${ batchId }`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<ClaimBatch>(url.toString());
	}

	getBatchesForIssueBatches(listOfIssueBatch: QualityBatch[] | undefined) {
		if (listOfIssueBatch === undefined) {
			return of(undefined);
		}
		return this.getBatches(
			[
				'id',
				'qtyBatchNet',
				'qtyBatchGross',
				'batchReference',
				'lastDispatchAdviceNumber',
				'lastDispatchAdviceDate'
			],
			batchesFilter(listOfIssueBatch),
			0,
			50,
			new OrderBy('batchReference', OrderByDirection.DESC),
			false
		)
			.pipe(catchError((err: unknown) => {
				console.error('Batch not found :', err);
				return of(undefined);
			}));
	}

	getBatchData(soldToTransacs: string[], shipToTransacs: string[], batchId: string) {
		let allNode: FieldExpression | NodeExpression = {
			field: 'id',
			term: phrase.escape(batchId),
			quoted: true
		};

		const transacNode = this.claimService.createTransacSoldtoAndShiptoFilter(soldToTransacs, shipToTransacs);

		allNode = {
			left: allNode,
			operator: 'AND',
			parenthesized: false,
			right: transacNode
		};

		return this.getBatchById(
			batchId,
			[
				'id',
				'batchReference',
				'supplierOrderNumber',
				'supplierOrderItemNumber',
				'millOrderNumber',
				'millOrderItemNumber',
				'soldTo',
				'soldToDescription',
				'shipTo',
				'qtyInvoiced',
				'weightUnitInvoiced',
				'currencyInvoiced',
				'sfcC',
				'thicknessDimension',
				'widthDimension',
				'lengthDimension',
				'qtyBatchGross',
				'qtyBatchNet',
				'steelgradeDescription',
				'cddWeek',
				'millDescription',
				'customerOrderNumber',
				'customerOrderItemNumber',
				'batchInvoiced'
			],
			luceneToString(allNode),
			1,
			10,
			new OrderBy('cdd', OrderByDirection.DESC),
			false
		)
			.pipe(
				tap(
					batchData => {
						const batchSoldTo = unpadSoldto(batchData.soldTo);
						const batchShipTo = unpadSoldto(batchData.shipTo);
						batchData.isClaimable = soldToTransacs.includes(batchSoldTo)
							|| shipToTransacs.includes(batchShipTo);
					}
				),
				map(batchData => <const>{ batchData, soldToTransacs })
			);
	}
}
